import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft } from "lucide-react";
import { RegblButton } from "@/components/RegblButton";

const TYPES = [
  { value: "entree", label: "Entrée" },
  { value: "sortie", label: "Sortie" },
  { value: "entree_sortie", label: "Entrée + sortie combinés" },
];

const MODULES = [
  ["deuxieme_salle_eau", "Deuxième salle d'eau"],
  ["wc_separe", "WC séparé"],
  ["balcon", "Balcon"],
  ["terrasse", "Terrasse"],
  ["jardin", "Jardin"],
  ["cave", "Cave"],
  ["galetas", "Galetas"],
  ["buanderie", "Buanderie privative"],
  ["place_parc", "Place de parc"],
  ["box", "Box"],
  ["reduit", "Réduit"],
];

export default function ConstatCreate() {
  const navigate = useNavigate();
  const [typologies, setTypologies] = useState([]);
  const [typologieId, setTypologieId] = useState("");
  const [typeConstat, setTypeConstat] = useState("entree");
  const [numeroDossier, setNumeroDossier] = useState("");
  const [immeuble, setImmeuble] = useState("");
  const [egid, setEgid] = useState("");
  const [regbl, setRegbl] = useState(null);
  const [modules, setModules] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/typologies").then((r) => setTypologies(r.data));
  }, []);

  const toggleModule = (key) => setModules((m) => ({ ...m, [key]: !m[key] }));

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/etats", {
        type_constat: typeConstat,
        typologie_id: typologieId || null,
        modules,
        egid,
        numero_dossier: numeroDossier,
        immeuble,
        npa: regbl?.npa || "",
        localite: regbl?.localite || "",
      });
      toast.success("Constat créé");
      navigate(`/constats/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate("/constats")} className="flex items-center gap-2 text-dd-blue mb-5 text-sm">
        <ArrowLeft size={16} /> Retour aux constats
      </button>
      <h1 className="text-3xl font-bold text-dd-blue mb-1">Nouveau constat</h1>
      <p className="text-sm text-dd-warm mb-8">
        Choisissez une typologie : les pièces et toutes leurs lignes seront générées automatiquement
        depuis le catalogue.
      </p>

      <div className="bg-white rounded-lg border border-dd-light p-6 space-y-6">
        <div>
          <Label className="text-dd-night mb-2 block">Type de constat</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {TYPES.map((t) => (
              <button key={t.value} data-testid={`type-${t.value}`}
                onClick={() => setTypeConstat(t.value)}
                className={`h-11 rounded-md text-sm font-medium border transition-colors ${
                  typeConstat === t.value ? "bg-dd-blue text-white border-dd-blue" : "bg-white text-dd-night border-dd-light hover:bg-dd-light/50"
                }`}>{t.label}</button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-dd-night mb-2 block">Typologie</Label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {typologies.map((t) => (
              <button key={t.id} data-testid={`typologie-${t.id}`}
                onClick={() => setTypologieId(t.id)}
                className={`h-11 rounded-md text-sm font-medium border transition-colors ${
                  typologieId === t.id ? "bg-dd-blue text-white border-dd-blue" : "bg-white text-dd-night border-dd-light hover:bg-dd-light/50"
                }`}>{t.libelle}</button>
            ))}
          </div>
          {typologies.length === 0 && (
            <p className="text-sm text-destructive mt-2">
              Aucune typologie. L'administrateur doit initialiser les données de référence (Réglages).
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-dd-night">N° EGID</Label>
            <div className="mt-1 flex gap-2">
              <Input data-testid="create-egid" value={egid} onChange={(e) => setEgid(e.target.value)} className="h-11" inputMode="numeric" />
              <RegblButton egid={egid} onResult={(r) => { setImmeuble(r.immeuble); setRegbl(r); }} />
            </div>
            {regbl && <p className="text-xs text-dd-warm mt-1" data-testid="regbl-result">{regbl.npa} {regbl.localite} — NPA/localité seront repris dans le constat</p>}
          </div>
          <div>
            <Label className="text-dd-night">N° de dossier</Label>
            <Input data-testid="create-dossier" value={numeroDossier} onChange={(e) => setNumeroDossier(e.target.value)} className="mt-1 h-11" />
          </div>
          <div>
            <Label className="text-dd-night">Immeuble</Label>
            <Input data-testid="create-immeuble" value={immeuble} onChange={(e) => setImmeuble(e.target.value)} className="mt-1 h-11" />
          </div>
        </div>

        <div>
          <Label className="text-dd-night mb-2 block">Modules optionnels</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {MODULES.map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-dd-night cursor-pointer">
                <Checkbox data-testid={`module-${key}`} checked={!!modules[key]} onCheckedChange={() => toggleModule(key)} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <Button data-testid="create-submit" disabled={saving || !typologieId} onClick={create}
          className="h-11 bg-dd-blue hover:bg-dd-night text-white">
          {saving ? "Création…" : "Créer le constat"}
        </Button>
      </div>
    </div>
  );
}
